import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from './ui/button';
import { X, Zap, Keyboard } from 'lucide-react';
import { toast } from 'sonner';
import { BrowserMultiFormatReader } from '@zxing/browser';

// 🔒 EAN PRÜFZIFFER-VALIDIERUNG (GS1 Standard)
// Gibt true zurück, wenn die EAN eine gültige Prüfziffer hat.
const validateEANChecksum = (ean) => {
  if (!ean || (ean.length !== 8 && ean.length !== 13)) return false;
  const digits = ean.split('').map(Number);
  const checkDigit = digits[digits.length - 1];
  const data = digits.slice(0, -1);

  let sum = 0;
  if (ean.length === 13) {
    // EAN-13: weights 1,3,1,3,... from left
    data.forEach((d, i) => { sum += d * (i % 2 === 0 ? 1 : 3); });
  } else {
    // EAN-8: weights 3,1,3,1,... from left
    data.forEach((d, i) => { sum += d * (i % 2 === 0 ? 3 : 1); });
  }
  const expected = (10 - (sum % 10)) % 10;
  return expected === checkDigit;
};

// 🔧 FEATURE FLAGS
const ENABLE_ROTATION_FALLBACK = true;  // Rotation-Fallback aktivieren (default: true)
const ROTATION_DELAY_MS = 1200;          // Warte 1.2s vor erstem Rotation-Versuch
const ROTATION_INTERVAL_MS = 800;        // Alle 800ms einen Rotation-Versuch
const EAN8_ENABLE_DELAY_MS = 4000;       // EAN-8 erst nach 4s aktivieren (EAN-13 Priorität)

/**
 * FULLSCREEN EAN SCANNER mit html5-qrcode
 * 
 * REQUIREMENTS:
 * - Fullscreen overlay (position: fixed, inset: 0)
 * - Video fills screen (100% width/height, object-fit: cover)
 * - Fokussierte Scanbox für EAN (65% Breite, 30% Höhe, mittig)
 * - EAN-13, EAN-8, CODE_128, UPC_A support
 * - fps: 15, single-shot autofocus (Priorität)
 * - iOS playsinline, clean cleanup
 * - Torch button if available
 */
export const BarcodeScanner = ({ 
  onDetected, 
  onClose
}) => {
  const [error, setError] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [cameraBlack, setCameraBlack] = useState(false); // iOS Schwarzer Bildschirm erkannt
  const [isInitializing, setIsInitializing] = useState(true);
  const [focusIndicator, setFocusIndicator] = useState(null);
  const [hintText, setHintText] = useState('Barcode wird gesucht...');
  const retryCountRef = useRef(0);
  const scannerRef = useRef(null);
  const isRunningRef = useRef(false);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const videoTrackRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const boundingBoxTimeoutRef = useRef(null);
  
  // 🔒 SINGLE-SCAN LOCK - Verhindert Mehrfach-Verarbeitung
  const scanLockedRef = useRef(false);
  const lastScannedCodeRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  
  // 🔄 ROTATION FALLBACK - für Hochkant/schräge Barcodes
  const rotationFallbackTimerRef = useRef(null);
  const rotationIntervalRef = useRef(null);
  const lastRotationAttemptRef = useRef(0);
  const ean8TimerRef = useRef(null);

  // 📦 BOUNDING BOX ZEICHNEN
  const drawBoundingBox = (decodeResult) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Canvas an Container-Größe anpassen
    const container = document.getElementById("barcode-reader-container");
    if (!container) return;
    
    const videoElement = container.querySelector('video');
    if (!videoElement) return;
    
    // Canvas-Größe = Video-Größe
    canvas.width = videoElement.videoWidth || container.clientWidth;
    canvas.height = videoElement.videoHeight || container.clientHeight;
    
    // Canvas Display-Größe = Container-Größe
    canvas.style.width = `${container.clientWidth}px`;
    canvas.style.height = `${container.clientHeight}px`;
    
    // Clear previous drawing
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Get corner points (oder result.boundingBox)
    const result = decodeResult?.result || decodeResult;
    let points = null;
    
    // Versuche verschiedene Strukturen
    if (result?.cornerPoints && result.cornerPoints.length >= 4) {
      points = result.cornerPoints;
    } else if (result?.boundingBox) {
      // Fallback: Bounding Box zu Punkten konvertieren
      const box = result.boundingBox;
      points = [
        { x: box.x, y: box.y },
        { x: box.x + box.width, y: box.y },
        { x: box.x + box.width, y: box.y + box.height },
        { x: box.x, y: box.y + box.height }
      ];
    }
    
    if (!points || points.length < 4) {
      console.log('⚠️ Keine Koordinaten für Bounding Box gefunden');
      return;
    }
    
    console.log('📦 Zeichne Bounding Box mit Punkten:', points);
    
    // Berechne Skalierungsfaktor (Video-Auflösung zu Canvas-Display)
    const scaleX = canvas.width / container.clientWidth;
    const scaleY = canvas.height / container.clientHeight;
    
    // Zeichne grünes Rechteck mit Glow-Effekt
    ctx.strokeStyle = "#00FF00";
    ctx.lineWidth = 4;
    ctx.shadowColor = "#00FF00";
    ctx.shadowBlur = 20;
    ctx.setLineDash([]);
    
    // Polygon zeichnen
    ctx.beginPath();
    ctx.moveTo(points[0].x / scaleX, points[0].y / scaleY);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x / scaleX, points[i].y / scaleY);
    }
    ctx.closePath();
    ctx.stroke();
    
    // Corner-Punkte (optional)
    ctx.fillStyle = "#00FF00";
    ctx.shadowBlur = 10;
    points.forEach(point => {
      ctx.beginPath();
      ctx.arc(point.x / scaleX, point.y / scaleY, 6, 0, 2 * Math.PI);
      ctx.fill();
    });
    
    // "✓ ERKANNT" Label
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(0, 255, 0, 0.9)";
    ctx.font = "bold 24px Arial";
    ctx.textAlign = "center";
    const centerX = points.reduce((sum, p) => sum + p.x / scaleX, 0) / points.length;
    const centerY = points.reduce((sum, p) => sum + p.y / scaleY, 0) / points.length;
    ctx.fillText("✓ ERKANNT", centerX, centerY - 20);
  };

  // 🔄 ROTATION FALLBACK - Offline-Decode mit 4 Rotationen (FÜR EAN-13/UPC/CODE128!)
  const tryRotationFallback = async () => {
    if (!ENABLE_ROTATION_FALLBACK) return;
    if (scanLockedRef.current) return;  // Bereits gefunden
    if (!videoTrackRef.current) return;
    
    const now = Date.now();
    if (now - lastRotationAttemptRef.current < ROTATION_INTERVAL_MS) return;
    lastRotationAttemptRef.current = now;
    
    try {
      // Hole aktuelles Video-Frame
      const video = document.querySelector('#barcode-reader-container video');
      if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return;
      
      // Canvas für Frame-Capture
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = video.videoWidth;
      tempCanvas.height = video.videoHeight;
      const ctx = tempCanvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // 🎯 PRIORITÄT 1: Native BarcodeDetector (Chrome/Edge/Android)
      if ('BarcodeDetector' in window) {
        const barcodeDetector = new window.BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'upc_a', 'code_128']
        });
        
        // Versuche 4 Rotationen: 0°, 90°, 180°, 270°
        const rotations = [0, 90, 180, 270];
        
        for (const angle of rotations) {
          const rotatedCanvas = document.createElement('canvas');
          
          if (angle === 90 || angle === 270) {
            rotatedCanvas.width = tempCanvas.height;
            rotatedCanvas.height = tempCanvas.width;
          } else {
            rotatedCanvas.width = tempCanvas.width;
            rotatedCanvas.height = tempCanvas.height;
          }
          
          const rotCtx = rotatedCanvas.getContext('2d');
          rotCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
          rotCtx.rotate((angle * Math.PI) / 180);
          rotCtx.drawImage(tempCanvas, -tempCanvas.width / 2, -tempCanvas.height / 2);
          
          const barcodes = await barcodeDetector.detect(rotatedCanvas);
          
          if (barcodes && barcodes.length > 0) {
            const code = barcodes[0].rawValue;
            console.log(`✅ BarcodeDetector Rotation SUCCESS @ ${angle}°:`, code);
            
            if (onDetected && !scanLockedRef.current) {
              scanLockedRef.current = true;
              onDetected(code);
              
              // Cleanup
              if (rotationFallbackTimerRef.current) {
                clearTimeout(rotationFallbackTimerRef.current);
              }
              if (rotationIntervalRef.current) {
                clearInterval(rotationIntervalRef.current);
              }
            }
            return;
          }
        }
      } else {
        // 🎯 FALLBACK: ZXing (iOS Safari / Firefox)
        const codeReader = new BrowserMultiFormatReader();
        
        const rotations = [0, 90, 180, 270];
        
        for (const angle of rotations) {
          const rotatedCanvas = document.createElement('canvas');
          
          if (angle === 90 || angle === 270) {
            rotatedCanvas.width = tempCanvas.height;
            rotatedCanvas.height = tempCanvas.width;
          } else {
            rotatedCanvas.width = tempCanvas.width;
            rotatedCanvas.height = tempCanvas.height;
          }
          
          const rotCtx = rotatedCanvas.getContext('2d');
          rotCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
          rotCtx.rotate((angle * Math.PI) / 180);
          rotCtx.drawImage(tempCanvas, -tempCanvas.width / 2, -tempCanvas.height / 2);
          
          try {
            const result = await codeReader.decodeFromCanvas(rotatedCanvas);
            
            if (result && result.text) {
              console.log(`✅ ZXing Rotation SUCCESS @ ${angle}°:`, result.text);
              
              if (onDetected && !scanLockedRef.current) {
                scanLockedRef.current = true;
                onDetected(result.text);
                
                // Cleanup
                if (rotationFallbackTimerRef.current) {
                  clearTimeout(rotationFallbackTimerRef.current);
                }
                if (rotationIntervalRef.current) {
                  clearInterval(rotationIntervalRef.current);
                }
              }
              return;
            }
          } catch (err) {
            // Decode failed für diesen Winkel, nächster Versuch
          }
        }
      }
    } catch (err) {
      console.error('Rotation-Fallback error:', err);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    
    const startScanner = async () => {
      try {
        // WAIT for DOM to be ready
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Check if component still mounted
        if (!mountedRef.current) return;
        
        // PRE-CHECK: MediaDevices API availability
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.error("MediaDevices API not available");
          if (mountedRef.current) {
            setError("Ihr Browser unterstützt keinen Kamerazugriff.\n\nBitte verwenden Sie einen modernen Browser (Chrome, Safari, Edge).");
            toast.error('Browser-Unterstützung fehlt');
          }
          return;
        }
        
        console.log("✅ MediaDevices API available");
        
        // Verify DOM container exists
        const container = document.getElementById("barcode-reader-container");
        if (!container) {
          console.error("Scanner container not found");
          if (mountedRef.current) {
            setError("Scanner konnte nicht initialisiert werden.");
          }
          return;
        }
        
        // Supported formats - 🏷️ EAN-13 PRIORITÄT: Startet ohne EAN_8
        // EAN_8 wird erst nach EAN8_ENABLE_DELAY_MS aktiviert um Fehllesungen zu vermeiden
        const formatsToSupport = [
          Html5QrcodeSupportedFormats.EAN_13,
          // EAN_8 wird dynamisch nach Verzögerung hinzugefügt (siehe ean8Timer unten)
        ];
        
        // Initialize Html5Qrcode directly (not Html5QrcodeScanner)
        let scanner = new Html5Qrcode("barcode-reader-container", { 
          verbose: false,
          formatsToSupport: formatsToSupport
        });
        scannerRef.current = scanner;
        
        // OPTIMIZED CONFIG for FULLSCREEN EAN SCANNING
        const config = {
          fps: 15,
          qrbox: function(viewfinderWidth, viewfinderHeight) {
            const width = Math.floor(viewfinderWidth * 0.85);
            const height = Math.floor(viewfinderHeight * 0.25);
            return { width, height };
          },
          // KEIN aspectRatio: 1.0 → lässt das native Seitenverhältnis der Kamera (wichtig für iOS PWA)
          disableFlip: false,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          },
          rememberLastUsedCamera: true,
        };
        
        // RETRY LOGIC: Try multiple camera configurations with OPTIMIZED FOCUS
        let scannerStarted = false;
        const cameraConfigs = [
          // Try 1: OPTIMIERT für Barcodes - single-shot + feste Distanz (10-30cm)
          { 
            facingMode: "environment",
            focusMode: "single-shot",
            focusDistance: 0.3  // 30cm optimal für Barcode-Scanning
          },
          // Try 2: Fallback ohne focusDistance (single-shot only)
          { 
            facingMode: "environment",
            focusMode: "single-shot"
          },
          // Try 3: Fallback zu continuous (aktuell - langsamer)
          { 
            facingMode: "environment"
          },
          // Try 4: Generic fallback
          { 
            facingMode: { ideal: "environment" }
          },
          // Try 5: User camera (front) als letzte Option
          { 
            facingMode: "user"
          }
        ];
        
        for (let i = 0; i < cameraConfigs.length && !scannerStarted; i++) {
          // iosVideoObserver VOR try/catch deklarieren → in beiden Blöcken sichtbar
          let iosVideoObserver = null;
          try {
            console.log(`🎥 Attempting camera config ${i + 1}/${cameraConfigs.length}:`, cameraConfigs[i]);

            // ── iOS PWA FIX: MutationObserver setzt playsinline/muted SOFORT ──
            iosVideoObserver = new MutationObserver((mutations) => {
              mutations.forEach(m => {
                m.addedNodes.forEach(node => {
                  if (node.nodeType === 1) {
                    const videos = node.tagName === 'VIDEO' ? [node] : Array.from(node.querySelectorAll('video'));
                    videos.forEach(v => {
                      v.setAttribute('playsinline', 'true');
                      v.setAttribute('webkit-playsinline', 'true');
                      v.setAttribute('muted', 'true');
                      v.muted = true;
                      v.playsInline = true;
                      if (v.paused) {
                        v.play().catch(() => {});
                      }
                    });
                  }
                });
              });
            });
            const scannerContainer = document.getElementById("barcode-reader-container");
            if (scannerContainer) {
              iosVideoObserver.observe(scannerContainer, { childList: true, subtree: true });
            }
            
            await scanner.start(
              cameraConfigs[i],
              config,
              (decodedText, decodeResult) => {
            // SUCCESS CALLBACK mit EAN-Validierung und Bounding Box
            if (!mountedRef.current || !scannerRef.current) return;
            
            // 🔒 SINGLE-SCAN LOCK: Verhindert Mehrfach-Verarbeitung
            if (scanLockedRef.current) {
              console.log('⚠️ Scan bereits in Verarbeitung, ignoriere Duplikat');
              return;
            }
            
            // Validiere EAN-Format (muss 8 oder 13 Zeichen sein)
            const cleanEAN = decodedText.trim();
            if (cleanEAN.length !== 8 && cleanEAN.length !== 13) {
              console.log('⚠️ Ungültiger EAN-Code (falsche Länge):', cleanEAN);
              return; // Ignoriere und scanne weiter
            }
            
            // 🔒 PRÜFZIFFER-VALIDIERUNG (GS1 Standard)
            if (!validateEANChecksum(cleanEAN)) {
              console.log('⚠️ EAN Prüfziffer ungültig – mögliche Fehllesung:', cleanEAN);
              toast.warning(`Unvollständiger Barcode erkannt (${cleanEAN.length} Stellen – Prüfziffer fehlerhaft). Bitte erneut scannen.`);
              return; // Scan ablehnen – weiter scannen
            }
            
            // ⚠️ EAN-8 Hinweis (kurze Codes könnten abgeschnittene EAN-13 sein)
            const isEan8 = cleanEAN.length === 8;
            if (isEan8) {
              // EAN-8 nur akzeptieren wenn 4s Verzögerung abgelaufen (EAN-13 Priorität)
              const ean8Allowed = scannerRef.current?._ean8Enabled === true;
              if (!ean8Allowed) {
                console.log('⏳ EAN-8 noch nicht aktiviert – weiter scannen für EAN-13:', cleanEAN);
                toast.warning('Kurzer Code erkannt – EAN-13 wird bevorzugt. Bitte warten…', { duration: 2000 });
                return; // Weiter scannen
              }
              console.log('⚠️ EAN-8 erkannt (nach Verzögerung bestätigt):', cleanEAN);
            }
            
            // LOCK AKTIVIEREN - ab jetzt keine weiteren Scans mehr
            scanLockedRef.current = true;
            lastScannedCodeRef.current = cleanEAN;
            lastScanTimeRef.current = Date.now();
            
            console.log('✅ Gültiger Barcode erkannt:', cleanEAN);
            console.log('📦 Decode Result:', decodeResult);
            
            // ✨ BOUNDING BOX ZEICHNEN
            drawBoundingBox(decodeResult);
            
            // Vibration feedback (länger für besseres Feedback)
            if (navigator.vibrate) {
              navigator.vibrate(200);
            }
            
            toast.success('Barcode erkannt!');
            
            // Clear timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            
            // Zeige Bounding Box für 1 Sekunde, dann schließe
            if (boundingBoxTimeoutRef.current) {
              clearTimeout(boundingBoxTimeoutRef.current);
            }
            
            boundingBoxTimeoutRef.current = setTimeout(() => {
              // Stop scanner and callback
              const currentScanner = scannerRef.current;
              scannerRef.current = null;
              setIsRunning(false);
              isRunningRef.current = false;  // ← SYNC REF
              
              if (currentScanner && isRunningRef.current !== false) {
                currentScanner.stop().then(() => {
                  try { currentScanner.clear(); } catch (e) {
                    console.log('Clear error (expected):', e);
                  }
                  onDetected(cleanEAN);
                }).catch((err) => {
                  console.log('Stop error after scan (expected):', err);
                  onDetected(cleanEAN);
                });
              } else {
                onDetected(cleanEAN);
              }
            }, 800); // 800ms Delay für visuelle Bestätigung
          },
          () => {}  // Error callback - ignore NotFoundException
        );
        
        // SUCCESS: Scanner started
        scannerStarted = true;
        if (iosVideoObserver) iosVideoObserver.disconnect();
        console.log(`✅ Scanner started successfully with camera config ${i + 1}`);
        
        // MARK AS RUNNING AFTER START SUCCEEDS
        if (mountedRef.current) {
          setIsRunning(true);
          isRunningRef.current = true;  // ← SYNC REF
          setIsInitializing(false);  // ← NEU: Initialisierung abgeschlossen
          console.log('✅ Scanner fully initialized');
          
          // 🔄 ROTATION FALLBACK - Starte nach Delay
          if (ENABLE_ROTATION_FALLBACK) {
            rotationFallbackTimerRef.current = setTimeout(() => {
              console.log('🔄 Starting rotation fallback...');
              rotationIntervalRef.current = setInterval(tryRotationFallback, ROTATION_INTERVAL_MS);
            }, ROTATION_DELAY_MS);
          }
        }
        
        break; // Exit retry loop on success
        
      } catch (startErr) {
        console.error(`❌ Camera config ${i + 1}/${cameraConfigs.length} failed:`, startErr);
        if (iosVideoObserver) iosVideoObserver.disconnect();
        console.error('Error details:', {
          message: startErr.message,
          name: startErr.name,
          type: typeof startErr,
          full: startErr
        });
        
        // CRITICAL: Clean up scanner before retry
        if (scannerRef.current) {
          try {
            console.log('🧹 Cleaning up scanner before retry...');
            await scanner.stop();
            await scanner.clear();
            console.log('✓ Scanner cleaned');
          } catch (cleanupErr) {
            console.log('Cleanup error (expected):', cleanupErr.message);
          }
        }
        
        // If this was the last attempt, throw detailed error
        if (i === cameraConfigs.length - 1) {
          const errorDetails = startErr.message || startErr.name || startErr.toString() || 'Unbekannter Fehler';
          throw new Error(`Kamera-Initialisierung fehlgeschlagen nach ${cameraConfigs.length} Versuchen.\n\nFehler: ${errorDetails}`);
        }
        
        // CRITICAL: Re-initialize scanner for next attempt
        console.log('🔄 Re-initializing scanner for next attempt...');
        const newScanner = new Html5Qrcode("barcode-reader-container", { 
          verbose: false,
          formatsToSupport: formatsToSupport
        });
        scannerRef.current = newScanner;
        scanner = newScanner;
        
        // Wait 1 second before next retry (give browser time to release camera)
        console.log('⏳ Waiting 1s before retry...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    if (!scannerStarted) {
      throw new Error("Scanner konnte nicht gestartet werden");
    }
    
    // 🏷️ EAN-8 VERZÖGERUNG: Nach 4 Sekunden EAN-8 als Fallback aktivieren
    // Dadurch wird EAN-13 bevorzugt erkannt, EAN-8 nur wenn wirklich nötig
    ean8TimerRef.current = setTimeout(() => {
      if (isRunningRef.current && scannerRef.current) {
        console.log('⏱️ EAN-8 Fallback aktiviert (nach 4s)');
        scannerRef.current._ean8Enabled = true;
      }
    }, EAN8_ENABLE_DELAY_MS);
    
    // NEU: Auto-Retry Logic - Scanner versucht automatisch neu mit anderen Settings
    let retryTimer = setTimeout(async () => {
      if (!mountedRef.current || !scannerRef.current || !isRunningRef.current) return;
      
      retryCountRef.current++;
      console.log(`🔄 Auto-Retry ${retryCountRef.current}/3: Versuche mit neuen Settings...`);
      
      // Hint-Text ändern
      if (retryCountRef.current === 1) {
        setHintText('Barcode nicht gefunden? Versuche erneut...');
      } else if (retryCountRef.current === 2) {
        setHintText('Immer noch nicht? Nutze Button unten!');
      }
      
      // Versuche Scanner mit anderen Settings neu zu starten
      try {
        const currentScanner = scannerRef.current;
        if (currentScanner && isRunningRef.current) {
          // Stop current scan
          await currentScanner.stop();
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Restart with different FPS
          const retryFps = retryCountRef.current === 1 ? 120 : 30;
          console.log(`Restarting with FPS: ${retryFps}`);
          
          await currentScanner.start(
            { facingMode: "environment" },
            { ...config, fps: retryFps },
            (decodedText) => {
              // Same success callback mit SINGLE-SCAN LOCK
              if (!mountedRef.current || !scannerRef.current) return;
              
              // 🔒 SINGLE-SCAN LOCK
              if (scanLockedRef.current) {
                console.log('⚠️ Scan bereits in Verarbeitung, ignoriere Duplikat');
                return;
              }
              
              const cleanEAN = decodedText.trim();
              if (cleanEAN.length !== 8 && cleanEAN.length !== 13) {
                console.log('⚠️ Ungültiger EAN-Code (falsche Länge):', cleanEAN);
                return;
              }
              
              // LOCK AKTIVIEREN
              scanLockedRef.current = true;
              lastScannedCodeRef.current = cleanEAN;
              lastScanTimeRef.current = Date.now();
              
              console.log('✅ Gültiger Barcode erkannt:', cleanEAN);
              
              if (navigator.vibrate) {
                navigator.vibrate(200);
              }
              
              toast.success('Barcode erkannt!');
              
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              
              const scanner = scannerRef.current;
              scannerRef.current = null;
              setIsRunning(false);
              isRunningRef.current = false;
              
              if (scanner) {
                scanner.stop().then(() => {
                  try { scanner.clear(); } catch (e) {
                    console.log('Clear error (expected):', e);
                  }
                  onDetected(cleanEAN);
                }).catch((err) => {
                  console.log('Stop error after scan (expected):', err);
                  onDetected(cleanEAN);
                });
              } else {
                onDetected(cleanEAN);
              }
            },
            () => {}
          );
          
          isRunningRef.current = true;
          console.log(`✓ Scanner restarted with FPS ${retryFps}`);
        }
      } catch (retryErr) {
        console.log('Auto-retry failed:', retryErr);
      }
      
      // Schedule next retry (max 2 retries = 3 total attempts)
      if (retryCountRef.current < 2 && mountedRef.current) {
        retryTimer = setTimeout(() => {}, 5000); // Next retry after 5 seconds
      }
    }, 5000); // First retry after 5 seconds
        
        // Post-start optimizations
        setTimeout(async () => {
          try {
            const container = document.getElementById("barcode-reader-container");

            // ── iOS Schwarzer-Bildschirm-Detektor startet IMMER (auch wenn videoElement noch null) ──
            // Video-Element wird ggf. erst nach kurzer Zeit eingefügt
            const waitForVideo = async (maxMs = 3000) => {
              const start = Date.now();
              while (Date.now() - start < maxMs) {
                const v = container?.querySelector('video');
                if (v) return v;
                await new Promise(r => setTimeout(r, 200));
              }
              return null;
            };

            const videoElement = await waitForVideo(3000) || container?.querySelector('video');
            
            if (videoElement) {
              // iOS PWA: Nochmals setzen + play() auslösen (Absicherung)
              videoElement.setAttribute('playsinline', 'true');
              videoElement.setAttribute('webkit-playsinline', 'true');
              videoElement.setAttribute('muted', 'true');
              videoElement.muted = true;
              videoElement.playsInline = true;
              if (videoElement.paused) {
                try { await videoElement.play(); } catch (e) { /* normal */ }
              }

              // ── iOS Schwarzer-Bildschirm-Detektor ────────────────────────────
              // Überprüft alle 1s ob das Video wirklich Frames liefert.
              // Nach 3s (3 × 1s) → AUTOMATISCHER Neustart (kein Button nötig)
              // Nach 6s → Button anzeigen als Fallback
              let blackScreenCheckCount = 0;
              let blackScreenAutoRestartDone = false;
              const MAX_BLACK_SCREEN_CHECKS = 6;       // 6 × 1s = 6s Gesamtzeit
              const AUTO_RESTART_THRESHOLD = 3;         // Nach 3s → automatisch neustarten

              const blackScreenChecker = setInterval(async () => {
                if (!mountedRef.current || !scannerRef.current) {
                  clearInterval(blackScreenChecker);
                  return;
                }
                if (blackScreenCheckCount >= MAX_BLACK_SCREEN_CHECKS) {
                  clearInterval(blackScreenChecker);
                  // Letzter Fallback: Button anzeigen
                  if (mountedRef.current) setCameraBlack(true);
                  return;
                }

                const v = container?.querySelector('video');
                if (!v) { clearInterval(blackScreenChecker); return; }

                const isPlaying = (
                  !v.paused && !v.ended &&
                  v.readyState >= 2 &&
                  v.videoWidth > 0 &&
                  v.videoHeight > 0
                );

                if (!isPlaying) {
                  blackScreenCheckCount++;
                  console.log(`[Scanner] Schwarzer Bildschirm (${blackScreenCheckCount}/${MAX_BLACK_SCREEN_CHECKS})`);
                  
                  // Erst play() versuchen
                  try { await v.play(); } catch { /* ignorieren */ }

                  // Nach AUTO_RESTART_THRESHOLD Checks → Scanner AUTOMATISCH neustarten
                  if (blackScreenCheckCount >= AUTO_RESTART_THRESHOLD && !blackScreenAutoRestartDone) {
                    blackScreenAutoRestartDone = true;
                    clearInterval(blackScreenChecker);
                    console.log('[Scanner] Auto-Restart wegen schwarzem Bildschirm...');
                    try {
                      const s = scannerRef.current;
                      if (s) {
                        await s.stop().catch(() => {});
                        try { s.clear(); } catch { /* ignorieren */ }
                      }
                      scannerRef.current = null;
                      setIsRunning(false);
                      isRunningRef.current = false;

                      // Kurze Pause dann neu starten
                      await new Promise(r => setTimeout(r, 800));
                      if (mountedRef.current) {
                        // Neuen Scanner mit einfachster Konfiguration starten
                        const freshScanner = new Html5Qrcode("barcode-reader-container", { verbose: false });
                        scannerRef.current = freshScanner;
                        await freshScanner.start(
                          { facingMode: "environment" },
                          { fps: 10, qrbox: { width: Math.floor(window.innerWidth * 0.8), height: Math.floor(window.innerHeight * 0.2) } },
                          (code) => { if (code && code.trim().length >= 8 && mountedRef.current) { freshScanner.stop().catch(()=>{}); onDetected(code.trim()); } },
                          () => {}
                        );
                        setIsRunning(true);
                        isRunningRef.current = true;
                        setCameraBlack(false);
                        console.log('[Scanner] Auto-Restart erfolgreich');
                      }
                    } catch (restartErr) {
                      console.error('[Scanner] Auto-Restart fehlgeschlagen:', restartErr);
                      if (mountedRef.current) setCameraBlack(true);
                    }
                  }
                } else {
                  // Video läuft → Checker stoppen
                  setCameraBlack(false);
                  clearInterval(blackScreenChecker);
                  console.log('[Scanner] Video läuft korrekt ✓');
                }
              }, 1000);
              
              if (videoElement.srcObject) {
              const track = videoElement.srcObject.getVideoTracks()[0];
              videoTrackRef.current = track;
              
              if (track && track.getCapabilities) {
                const capabilities = track.getCapabilities();
                if (capabilities.torch) {
                  setTorchAvailable(true);
                }
                const constraints = {};
                if (capabilities.focusMode?.includes('single-shot')) {
                  constraints.focusMode = 'single-shot';
                } else if (capabilities.focusMode?.includes('continuous')) {
                  constraints.focusMode = 'continuous';
                }
                if (capabilities.focusDistance) {
                  constraints.focusDistance = 0.3;
                }
                if (capabilities.exposureMode?.includes('continuous')) {
                  constraints.exposureMode = 'continuous';
                }
                if (capabilities.whiteBalanceMode?.includes('continuous')) {
                  constraints.whiteBalanceMode = 'continuous';
                }
                if (Object.keys(constraints).length > 0) {
                  await track.applyConstraints({ advanced: [constraints] });
                }
              }
            }
            }
          } catch (camErr) {
            console.log("Camera optimization:", camErr.message);
          }
        }, 800);
        
        if (mountedRef.current) {
          setScanning(true);
          
          // Show manual input after 12 seconds
          timeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setShowManualInput(true);
            }
          }, 12000);
        }
      } catch (err) {
        console.error("Scanner error:", err);
        if (mountedRef.current) {
          if (err.name === 'NotAllowedError') {
            setError("Kamera-Zugriff verweigert.\n\nBitte erlaube den Zugriff in den Einstellungen.");
            toast.error('Kamera-Zugriff verweigert');
          } else if (err.name === 'NotFoundError') {
            setError("Keine Kamera gefunden.");
            toast.error('Keine Kamera gefunden');
          } else {
            // Detailed error message for debugging
            const errorMsg = err.message || 'Unbekannter Fehler';
            console.error("Scanner initialization failed:", errorMsg);
            setError(`Scanner-Fehler:\n\n${errorMsg}\n\nBitte versuche:\n1. Seite neu laden\n2. Browser-Cache leeren\n3. Anderen Browser verwenden`);
            toast.error('Scanner-Fehler');
          }
        }
      }
    };

    const timer = setTimeout(startScanner, 100);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (boundingBoxTimeoutRef.current) {
        clearTimeout(boundingBoxTimeoutRef.current);
      }
      
      // Cleanup rotation timers
      if (rotationFallbackTimerRef.current) {
        clearTimeout(rotationFallbackTimerRef.current);
      }
      if (rotationIntervalRef.current) {
        clearInterval(rotationIntervalRef.current);
      }
      // Cleanup EAN-8 delay timer
      if (ean8TimerRef.current) {
        clearTimeout(ean8TimerRef.current);
      }
      
      // Clear canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
      
      // SAFE CLEANUP - Use REF for synchronous check
      if (scannerRef.current && isRunningRef.current) {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        isRunningRef.current = false;
        scanLockedRef.current = false;  // Reset lock on cleanup
        
        // Stop with error handling - only if scanner is actually running
        try {
          scanner.stop()
            .then(() => {
              try { 
                scanner.clear(); 
              } catch (e) {
                console.log('Clear error (expected):', e);
              }
            })
            .catch((err) => {
              console.log('Stop error on cleanup (expected):', err.message);
              // Silently handle - component is unmounting anyway
            });
        } catch (syncErr) {
          console.log('Synchronous stop error (expected):', syncErr.message);
        }
      }
      
      // Turn off torch and stop track
      if (videoTrackRef.current) {
        try {
          if (torchOn) {
            videoTrackRef.current.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {});
          }
          videoTrackRef.current.stop();
        } catch (e) {
          console.log('Track stop error (expected):', e);
        }
      }
    };
  }, [onDetected]);

  const toggleTorch = async () => {
    if (!videoTrackRef.current) return;
    
    try {
      const track = videoTrackRef.current;
      const capabilities = track.getCapabilities();
      
      if (capabilities.torch) {
        await track.applyConstraints({
          advanced: [{ torch: !torchOn }]
        });
        setTorchOn(!torchOn);
        toast.info(torchOn ? 'Taschenlampe AUS' : 'Taschenlampe AN');
      }
    } catch (err) {
      console.error('Torch error:', err);
      toast.error('Taschenlampe konnte nicht aktiviert werden');
    }
  };

  // OPTIMIERT: Tap-to-Focus Handler mit single-shot
  const handleTapToFocus = async (event) => {
    // Touch-Events blockieren (verhindert versehentliches Zurück)
    event.preventDefault();
    event.stopPropagation();
    
    if (!videoTrackRef.current) return;
    
    try {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      
      // Visueller Focus-Indikator
      setFocusIndicator({ x: event.clientX - rect.left, y: event.clientY - rect.top });
      setTimeout(() => setFocusIndicator(null), 600);
      
      // Camera Focus API nutzen
      const track = videoTrackRef.current;
      const capabilities = track.getCapabilities();
      
      // OPTIMIERT: Bevorzuge single-shot für schnellen Focus
      const constraints = { advanced: [{}] };
      
      if (capabilities.focusMode?.includes('single-shot')) {
        // BEST: single-shot = schnelle, einmalige Fokussierung
        constraints.advanced[0].focusMode = 'single-shot';
        constraints.advanced[0].focusDistance = 0.3;  // 30cm optimal
        console.log('📸 Tap-to-Focus: single-shot mode');
      } else if (capabilities.focusMode?.includes('continuous')) {
        // Fallback: continuous
        constraints.advanced[0].focusMode = 'continuous';
        constraints.advanced[0].focusDistance = 0.3;
        console.log('📸 Tap-to-Focus: continuous mode (fallback)');
      }
      
      // pointsOfInterest für präzisen Tap-to-Focus (wenn unterstützt)
      if (capabilities.pointsOfInterest) {
        constraints.pointsOfInterest = [{ x: x / 100, y: y / 100 }];
        console.log('📍 Focus point:', { x: x / 100, y: y / 100 });
      }
      
      await track.applyConstraints(constraints);
      console.log('✅ Focus angepasst');
      
      // Vibriere für Feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    } catch (err) {
      console.log('Focus error (expected on some devices):', err);
    }
  };

  const handleManualInput = () => {
    // Cleanup rotation timers
    if (rotationFallbackTimerRef.current) {
      clearTimeout(rotationFallbackTimerRef.current);
    }
    if (rotationIntervalRef.current) {
      clearInterval(rotationIntervalRef.current);
    }
    
    // Stop scanner before closing
    if (scannerRef.current && isRunningRef.current) {
      const scanner = scannerRef.current;
      scannerRef.current = null;
      isRunningRef.current = false;
      scanLockedRef.current = false;  // Reset lock
      setIsRunning(false);
      
      try {
        scanner.stop()
          .catch((err) => console.log('Stop on manual input (expected):', err));
      } catch (syncErr) {
        console.log('Synchronous stop error on manual input (expected):', syncErr);
      }
    }
    
    onClose();
    toast.info('Bitte EAN manuell eingeben');
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleTapToFocus(e);
      }}
      onTouchStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      style={{ touchAction: 'none' }}
    >
      {/* FULLSCREEN Container */}
      
      {/* Header Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 bg-gray-800/80 backdrop-blur-sm h-14 flex items-center justify-between px-4">
        {/* Close Button links */}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="h-10 w-10 text-white hover:bg-white/10"
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Titel */}
        <span className="text-white font-semibold text-base">Scanner</span>

        {/* Torch Button rechts (nur wenn verfügbar) */}
        {torchAvailable && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleTorch();
            }}
            disabled={!scanning || error}
            className={`h-10 w-10 ${
              torchOn 
                ? 'bg-yellow-400 hover:bg-yellow-500 text-black' 
                : 'text-white hover:bg-white/10'
            }`}
          >
            <Zap className="h-5 w-5" />
          </Button>
        )}
        
        {!torchAvailable && <div className="w-10" />}
      </div>

      {/* Camera Container - FULLSCREEN */}
      <div className="absolute inset-0">
        {/* Html5Qrcode Container - ALWAYS PRESENT */}
        <div 
          id="barcode-reader-container" 
          className="w-full h-full"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: error ? 'none' : 'block'
          }}
        />
        
        {/* 📦 BOUNDING BOX CANVAS OVERLAY */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            zIndex: 25,
            display: error ? 'none' : 'block'
          }}
        />

        {/* 🔄 LOADING INDICATOR - Kamera wird gestartet */}
        {isInitializing && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm z-40">
            <div className="text-center space-y-4">
              {/* Spinner Animation */}
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-t-white border-r-white border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              </div>
              
              {/* Text */}
              <div className="space-y-2">
                <p className="text-white text-lg font-semibold">
                  Kamera wird gestartet
                </p>
                <p className="text-white/70 text-sm">
                  Bitte warten Sie einen Moment...
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          // Error State - OVERLAYS the container
          <div className="absolute inset-0 flex items-center justify-center h-full px-6 bg-gray-900 z-30">
            <div className="text-center max-w-md">
              <div className="bg-red-500/20 border-2 border-red-500 rounded-2xl p-6 mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Kamera-Fehler</h3>
                <p className="text-white/90 text-sm leading-relaxed whitespace-pre-line">{error}</p>
              </div>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleManualInput();
                }}
                className="w-full h-14 bg-white text-black text-base font-semibold hover:bg-gray-100"
              >
                <Keyboard className="h-5 w-5 mr-2" />
                Manuell eingeben
              </Button>
            </div>
          </div>
        )}

        {/* Hint Text - Over Video (Dynamisch) - NUR wenn NICHT initialisiert */}
        {!error && !isInitializing && (
          <div className="absolute top-20 left-0 right-0 z-20 text-center px-4 pointer-events-none">
            <div className="inline-block bg-black/80 backdrop-blur-sm px-5 py-3 rounded-lg shadow-lg">
              <p className="text-white text-sm font-semibold mb-1">
                {hintText}
              </p>
              <p className="text-white/80 text-xs leading-relaxed">
                Barcode vollständig in den Rahmen<br/>
                10–20 cm Abstand • Kurz ruhig halten
              </p>
              <p className="text-white/60 text-xs mt-1.5">
                Antippen zum Fokussieren
              </p>
            </div>
          </div>
        )}
        
        {/* Focus-Indikator (Tap-to-Focus visuelles Feedback) - NUR wenn NICHT initialisiert */}
        {focusIndicator && !isInitializing && (
          <div 
            className="absolute z-40 pointer-events-none"
            style={{
              left: focusIndicator.x,
              top: focusIndicator.y,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="w-16 h-16 border-2 border-white rounded-full animate-ping opacity-75"></div>
            <div className="absolute inset-0 w-16 h-16 border-2 border-white rounded-full"></div>
          </div>
        )}

        {/* "Nicht erkannt?" Button - Sofort sichtbar - NUR wenn NICHT initialisiert */}
        {!error && !isInitializing && (
          <div 
            className="absolute bottom-24 left-0 right-0 z-20 flex flex-col items-center gap-2 px-4"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            {/* iOS Schwarzer Bildschirm → Kamera neu starten Button */}
            {cameraBlack && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCameraBlack(false);
                  window.location.reload();
                }}
                className="h-14 px-6 bg-blue-500 hover:bg-blue-600 text-white text-base font-bold rounded-full shadow-lg w-full"
              >
                Kamera schwarz? Neu starten
              </Button>
            )}
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleManualInput();
              }}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
              className="h-14 px-8 bg-red-500/90 backdrop-blur-sm hover:bg-red-600 text-white text-base font-semibold rounded-full shadow-lg"
            >
              ❌ Nicht erkannt? Jetzt manuell eingeben
            </Button>
          </div>
        )}

        {/* Manual Input Button - Bottom */}
        {showManualInput && !error && (
          <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center px-4">
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleManualInput();
              }}
              className="h-14 px-8 bg-white hover:bg-gray-100 text-black text-base font-semibold shadow-2xl"
            >
              <Keyboard className="h-5 w-5 mr-2" />
              Manuell eingeben
            </Button>
          </div>
        )}
      </div>

      {/* Custom CSS for html5-qrcode FULLSCREEN */}
      <style jsx global>{`
        /* Ensure video fills FULLSCREEN */
        #barcode-reader-container video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
        }
        
        /* Hide default html5-qrcode UI elements */
        #barcode-reader-container__dashboard_section {
          display: none !important;
        }
        
        #barcode-reader-container__dashboard_section_csr {
          display: none !important;
        }
        
        /* Style the scan region (wide rectangle) */
        #barcode-reader-container__scan_region {
          border: 3px solid #ef4444 !important;
          border-radius: 8px !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5) !important;
        }
        
        /* Ensure container is fullscreen */
        #barcode-reader-container {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background: black !important;
        }
      `}</style>
    </div>
  );
};
